import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Leaf,
  User,
  Mail,
  Phone,
  MapPin,
  Loader2,
  CheckCircle,
  Building2,
  Scissors,
  Sparkles,
  Award,
  TrendingUp,
  Users,
  BookOpen,
  Heart,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

export default function DiventaPartnerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    cellulare: "",
    citta: "",
    regione: "",
    tipologia: "",
    nome_attivita: "",
    note: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.email || !formData.tipologia) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (!gdprConsent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/register-partner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          cellulare: formData.cellulare,
          citta: formData.citta,
          regione: formData.regione,
          tipologia: formData.tipologia,
          nome_attivita: formData.nome_attivita,
          note: formData.note,
          fonte: "diventa_partner"
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Richiesta inviata con successo!");
      } else {
        toast.error(data.detail || "Errore nell'invio della richiesta");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center shadow-xl">
          <CardContent className="pt-10 pb-8">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Richiesta Inviata!</h2>
            <p className="text-slate-600 mb-6">
              Grazie per il tuo interesse! Un nostro consulente ti contatterà entro 24-48 ore per illustrarti tutti i vantaggi della collaborazione.
            </p>
            <div className="space-y-3">
              <Link to="/benessere">
                <Button className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700">
                  Esplora i Contenuti
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Torna alla Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50" data-testid="diventa-partner-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">ODR Academy</span>
            </Link>
            <Link to="/benessere">
              <Button variant="outline" size="sm" className="rounded-full">
                Scopri il Benessere
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Side - Benefits */}
          <div className="lg:sticky lg:top-24">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-medium mb-4">
                <Award className="w-4 h-4" />
                Partnership Esclusiva
              </div>
              <h1 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Diventa Partner ODR Academy
              </h1>
              <p className="text-lg text-slate-600">
                Unisciti alla rete di professionisti del benessere e dell'estetica. Offri ai tuoi clienti contenuti esclusivi e formazione certificata.
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {[
                { 
                  icon: BookOpen, 
                  title: "Formazione Continua", 
                  desc: "Accesso a corsi e materiali formativi esclusivi per te e il tuo staff",
                  color: "bg-rose-100 text-rose-600"
                },
                { 
                  icon: TrendingUp, 
                  title: "Crescita del Business", 
                  desc: "Strumenti di marketing e contenuti per attrarre nuovi clienti",
                  color: "bg-pink-100 text-pink-600"
                },
                { 
                  icon: Users, 
                  title: "Network Professionale", 
                  desc: "Entra in contatto con altri professionisti del settore",
                  color: "bg-fuchsia-100 text-fuchsia-600"
                },
                { 
                  icon: Heart, 
                  title: "Supporto Dedicato", 
                  desc: "Un consulente personale per accompagnarti nella crescita",
                  color: "bg-purple-100 text-purple-600"
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
                  <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center flex-shrink-0`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-6 text-white">
              <p className="italic mb-4">
                "Da quando collaboro con ODR Academy, il mio centro ha aumentato del 40% i trattamenti legati al benessere. I clienti apprezzano la nostra competenza certificata."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold">Maria Rossi</p>
                  <p className="text-sm text-white/80">Centro Estetico Aurora, Milano</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-serif">Richiedi Informazioni</CardTitle>
              <CardDescription>
                Compila il form e verrai ricontattato entro 24-48 ore
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipologia Attività */}
                <div>
                  <Label>Tipologia Attività *</Label>
                  <Select onValueChange={(value) => handleSelectChange("tipologia", value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona la tua attività" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centro_estetico">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-rose-500" />
                          Centro Estetico
                        </div>
                      </SelectItem>
                      <SelectItem value="parrucchiere">
                        <div className="flex items-center gap-2">
                          <Scissors className="w-4 h-4 text-amber-500" />
                          Parrucchiere / Hair Stylist
                        </div>
                      </SelectItem>
                      <SelectItem value="spa_wellness">
                        <div className="flex items-center gap-2">
                          <Leaf className="w-4 h-4 text-teal-500" />
                          SPA / Centro Benessere
                        </div>
                      </SelectItem>
                      <SelectItem value="farmacia">
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-green-500" />
                          Farmacia / Parafarmacia
                        </div>
                      </SelectItem>
                      <SelectItem value="altro">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          Altro
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nome Attività */}
                <div>
                  <Label htmlFor="nome_attivita">Nome Attività</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="nome_attivita"
                      name="nome_attivita"
                      value={formData.nome_attivita}
                      onChange={handleChange}
                      placeholder="Es. Centro Estetico Aurora"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Nome e Cognome */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        placeholder="Mario"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="cognome"
                        name="cognome"
                        value={formData.cognome}
                        onChange={handleChange}
                        placeholder="Rossi"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@tuocentro.it"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Telefono */}
                <div>
                  <Label htmlFor="cellulare">Telefono</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="cellulare"
                      name="cellulare"
                      type="tel"
                      value={formData.cellulare}
                      onChange={handleChange}
                      placeholder="+39 333 1234567"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Città e Regione */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="citta">Città</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="citta"
                        name="citta"
                        value={formData.citta}
                        onChange={handleChange}
                        placeholder="Milano"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Regione</Label>
                    <Select onValueChange={(value) => handleSelectChange("regione", value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
                          "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
                          "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
                          "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
                        ].map((reg) => (
                          <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Note */}
                <div>
                  <Label htmlFor="note">Messaggio (opzionale)</Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Raccontaci del tuo centro e cosa ti interessa della partnership..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>

                {/* GDPR */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5"
                  />
                  <Label htmlFor="gdpr" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    Acconsento al trattamento dei miei dati personali ai sensi del GDPR per essere ricontattato da ODR Academy. 
                    <Link to="/privacy" className="text-rose-600 hover:underline ml-1">
                      Leggi l'informativa completa
                    </Link>
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg shadow-rose-500/25"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      Richiedi Informazioni
                      <Sparkles className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-slate-500">
                  Un nostro consulente ti contatterà entro 24-48 ore lavorative
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © 2025 ODR Academy - La Scienza del Benessere. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}
